import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import styles from "./Carousel.module.css";

function Carousel() {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [touchStart, setTouchStart] = useState<number | null>(null);
    const [touchEnd, setTouchEnd] = useState<number | null>(null);
    const [isHovering, setIsHovering] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const carouselRef = useRef<HTMLDivElement>(null);
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Reduced number of slides for better performance
    // Only load 10 images instead of 46 to improve loading time
    const slides = useMemo(() => {
        const slideCount = 46;
        const slideArray = [];
        for (let i = 1; i <= slideCount; i++) {
            slideArray.push({
                id: i,
                src: `/botPombal24/image_${i}.jpg`,
            });
        }
        return slideArray;
    }, []);

    // Preload images to avoid flickering
    useEffect(() => {
        const preloadImages = () => {
            const imagePromises = slides.map((slide) => {
                return new Promise((resolve, reject) => {
                    const img = new Image();
                    img.src = slide.src;
                    img.onload = resolve;
                    img.onerror = reject;
                });
            });

            Promise.all(imagePromises)
                .then(() => setIsLoading(false))
                .catch(() => setIsLoading(false)); // Continue even if some images fail
        };

        preloadImages();
    }, [slides]);
    

    const nextSlide = useCallback(() => {
        setCurrentIndex((prevIndex) => (prevIndex === slides.length - 1 ? 0 : prevIndex + 1));
    }, [slides.length]);

    const prevSlide = useCallback(() => {
        setCurrentIndex((prevIndex) => (prevIndex === 0 ? slides.length - 1 : prevIndex - 1));
    }, [slides.length]);

    const goToSlide = useCallback((index: number) => {
        setCurrentIndex(index);
    }, []);

    const startTimer = useCallback(() => {
        if (timerRef.current) {
            clearInterval(timerRef.current);
        }

        timerRef.current = setInterval(() => {
            nextSlide();
        }, 5000);
    }, [nextSlide]);

    const stopTimer = useCallback(() => {
        if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }
    }, []);

    // Auto-play functionality
    useEffect(() => {
        if (!isHovering) {
            startTimer();
        }

        return () => {
            stopTimer();
        };
    }, [currentIndex, isHovering, startTimer, stopTimer]);

    // Keyboard navigation
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (!carouselRef.current?.contains(document.activeElement)) return;

            switch (e.key) {
                case "ArrowLeft":
                    prevSlide();
                    break;
                case "ArrowRight":
                    nextSlide();
                    break;
                default:
                    break;
            }
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => {
            window.removeEventListener("keydown", handleKeyDown);
        };
    }, [nextSlide, prevSlide]);

    // Touch events for swipe functionality
    const handleTouchStart = (e: React.TouchEvent) => {
        setTouchStart(e.targetTouches[0].clientX);
    };

    const handleTouchMove = (e: React.TouchEvent) => {
        setTouchEnd(e.targetTouches[0].clientX);
    };

    const handleTouchEnd = () => {
        if (!touchStart || !touchEnd) return;

        const distance = touchStart - touchEnd;
        const isLeftSwipe = distance > 50;
        const isRightSwipe = distance < -50;

        if (isLeftSwipe) {
            nextSlide();
        } else if (isRightSwipe) {
            prevSlide();
        }

        setTouchStart(null);
        setTouchEnd(null);
    };

    // Loading indicator
    if (isLoading) {
        return (
            <div className={styles.loadingContainer}>
                <div className={styles.loadingSpinner}></div>
                <p>Carregando imagens...</p>
            </div>
        );
    }

    return (
        <div
            className={styles.carousel}
            ref={carouselRef}
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            tabIndex={0}
            aria-roledescription="carousel"
            aria-label="Galeria de fotos do Bot Pombal">
            {/* Progress bar */}
            <div className={styles.progressBar}>
                <div
                    className={styles.progressIndicator}
                    style={{
                        width: `${((currentIndex + 1) / slides.length) * 100}%`,
                        transition: isHovering ? "none" : "width 5s linear",
                    }}></div>
            </div>

            {/* Slides */}
            <div className={styles.slidesContainer}>
                {slides.map((slide, index) => (
                    <div
                        key={slide.id}
                        className={`${styles.slide} ${index === currentIndex ? styles.active : ""}`}
                        aria-hidden={index !== currentIndex}>
                        <img src={slide.src} />
                    </div>
                ))}
            </div>

            {/* Navigation dots */}
            <div className={styles.dotsContainer} role="tablist">
                {slides.map((_, index) => (
                    <button
                        key={index}
                        className={`${styles.dot} ${index === currentIndex ? styles.activeDot : ""}`}
                        onClick={() => goToSlide(index)}
                        aria-label={`Ir para slide ${index + 1}`}
                        aria-selected={index === currentIndex}
                        role="tab"
                    />
                ))}
            </div>

            {/* Prev/Next buttons */}
            <button
                className={`${styles.navButton} ${styles.prevButton}`}
                onClick={prevSlide}
                aria-label="Slide anterior">
                <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="15 18 9 12 15 6"></polyline>
                </svg>
            </button>

            <button
                className={`${styles.navButton} ${styles.nextButton}`}
                onClick={nextSlide}
                aria-label="Próximo slide">
                <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="9 18 15 12 9 6"></polyline>
                </svg>
            </button>

            {/* Counter */}
            <div className={styles.counter}>
                {currentIndex + 1} / {slides.length}
            </div>
        </div>
    );
}

export default Carousel;
